import Sheet from '../../shared/components/Sheet';
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getReceivingInvoice, postReceivingInvoice, cancelReceivingInvoice, ReceivingInvoice, ReceivingLine } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const ReceivingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { sessionToken, user } = useAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<ReceivingInvoice | null>(null);
  const [lines, setLines] = useState<ReceivingLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [posting, setPosting] = useState(false);
  const downloadOriginal = async () => {
    try {
      const response=await fetch(`/api/v2/invoice-files/${invoice?.invoice_file_id}`,{headers:{'X-Session-Token':sessionToken || ''},cache:'no-store'});
      if(!response.ok)throw new Error('Unable to download original invoice.');
      const blob=await response.blob();const url=URL.createObjectURL(blob);
      const link=document.createElement('a');link.href=url;link.download=`invoice-${invoice?.id}.${blob.type==='application/pdf'?'pdf':blob.type==='image/png'?'png':blob.type==='image/tiff'?'tiff':'jpg'}`;link.click();setTimeout(()=>URL.revokeObjectURL(url),1000);
    }catch(e){setError(e instanceof Error?e.message:'Unable to download invoice.');}
  };

  useEffect(() => {
    if (!id) return;
    getReceivingInvoice(sessionToken, parseInt(id))
      .then(data => { setInvoice(data.invoice); setLines(data.lines); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, id]);

  const handlePost = async () => {
    if (!id) return;
    setPosting(true);
    try {
      await postReceivingInvoice(sessionToken, parseInt(id));
      // Reload
      const data = await getReceivingInvoice(sessionToken, parseInt(id));
      setInvoice(data.invoice);
      setLines(data.lines);
      setReviewing(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPosting(false);
    }
  };

  const handleCancel = async () => {
    if (!id || !window.confirm('Cancel this invoice?')) return;
    try {
      await cancelReceivingInvoice(sessionToken, parseInt(id));
      navigate('/inventory/receiving');
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) return <div className="inv2-loading">Loading...</div>;
  if (error && !invoice) return <div className="inv2-error">{error}</div>;
  if (!invoice) return <div className="inv2-error">Invoice not found</div>;

  const canPost = invoice.status === 'draft' && ['Owner', 'Admin', 'Manager'].includes(user?.role || '');

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <div>
          <Link to="/inventory/receiving" className="inv2-back-link">← Back to Receiving</Link>
          <h1>Invoice {invoice.invoice_number || `#${invoice.id}`}</h1>
          <span className={`inv2-status inv2-status-${invoice.status}`}>{invoice.status}</span>
        </div>
        <div className="inv2-header-actions">
          {invoice.invoice_file_id && <button onClick={downloadOriginal} className="inv2-btn">Download original invoice</button>}
          {canPost && (
            <>
              <Link className="inv2-btn" to={`/inventory/receiving/${id}/edit`}>Edit draft</Link>
              <button onClick={() => setReviewing(true)} disabled={posting} className="inv2-btn inv2-btn-primary">
                {posting ? 'Posting...' : 'Review stock changes'}
              </button>
              <button onClick={handleCancel} className="inv2-btn inv2-btn-danger">Cancel Invoice</button>
            </>
          )}
        </div>
      </div>

      {error && <p className="inv2-error" role="alert">{error}</p>}
      {reviewing && <Sheet title="Review stock changes" onClose={() => setReviewing(false)} busy={posting}><p>{invoice.stock_effect==='history_only'?'Posting records invoice prices without changing stock.':'Posting adds these quantities to inventory. Confirm units and quantities before continuing.'}</p><ul>{lines.map(line => <li key={line.id}>{line.matched_item_name || line.raw_item_name || 'Unmatched item'}: +{line.quantity_received} {line.unit}</li>)}</ul>{error && <p role="alert">{error}</p>}<button disabled={posting} onClick={handlePost}>{posting ? 'Posting…' : 'Confirm and post invoice'}</button></Sheet>}
      {/* Invoice Header Info */}
      <div className="inv2-detail-grid">
        <div className="inv2-detail-section">
          <h3>Invoice Details</h3>
          <dl className="inv2-dl">
            <dt>Supplier</dt><dd>{invoice.supplier_name || '—'}</dd>
            <dt>Stock treatment</dt><dd>{invoice.stock_effect==='history_only'?'Price history only — stock already counted':'New delivery — adds to stock'}</dd>
            <dt>Invoice Number</dt><dd>{invoice.invoice_number || '—'}</dd>
            <dt>Invoice Date</dt><dd>{invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : '—'}</dd>
            <dt>Total</dt><dd>{invoice.total != null ? `$${Number(invoice.total).toFixed(2)}` : '—'}</dd>
            <dt>Tax</dt><dd>{invoice.tax != null ? `$${Number(invoice.tax).toFixed(2)}` : '—'}</dd>
            <dt>Discount</dt><dd>{invoice.discount != null ? `$${Number(invoice.discount).toFixed(2)}` : '—'}</dd>
            <dt>Status</dt><dd><span className={`inv2-status inv2-status-${invoice.status}`}>{invoice.status}</span></dd>
            {invoice.posted_at && <><dt>Posted At</dt><dd>{new Date(invoice.posted_at).toLocaleString()}</dd></>}
            <dt>Notes</dt><dd>{invoice.notes || '—'}</dd>
          </dl>
        </div>
      </div>

      {/* Line Items */}
      <div className="inv2-section">
        <h2>Line Items ({lines.length})</h2>
        <div className="inv2-table-container">
          <DataTable
            rows={lines}
            rowKey={(line) => line.id}
            caption="Invoice lines"
            moreLabel="Pricing"
            empty="No line items"
            columns={[
              { key: 'raw', header: 'Item (Invoice)', priority: 'primary', render: (line) => line.raw_item_name || '—' },
              {
                key: 'matched', header: 'Matched Item', priority: 'summary',
                render: (line) => (line.inventory_item_id ? (
                  <Link to={`/inventory/items/${line.inventory_item_id}`} className="inv2-link">
                    {line.item_name || line.matched_item_name || `Item #${line.inventory_item_id}`}
                  </Link>
                ) : '—'),
              },
              { key: 'qty', header: 'Quantity', priority: 'summary', numeric: true, render: (line) => `${Number(line.quantity_received).toFixed(1)} ${line.unit}` },
              { key: 'cost', header: 'Cost', priority: 'summary', numeric: true, render: (line) => `$${Number(line.purchase_cost).toFixed(2)}` },
              { key: 'match', header: 'Match Status', priority: 'summary', render: (line) => <span className={`inv2-match-status inv2-match-${line.match_status}`}>{line.match_status}</span> },
              { key: 'unit', header: 'Unit', priority: 'detail', render: (line) => line.unit },
              { key: 'uprice', header: 'Unit Price', priority: 'detail', numeric: true, render: (line) => `$${Number(line.unit_price).toFixed(4)}` },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default ReceivingDetail;
